import React, {
  useEffect,
  useState,
} from 'react';

import { GoogleLoginResponse } from 'react-google-login';
import { useDispatch } from 'react-redux';

import {
  Button,
  ButtonProps,
} from '@waglabs/button-next';
import { Grid } from '@waglabs/grid';
import { Typography } from '@waglabs/typography';

import { AppleLoginButton } from '../../../../../../components/AppleLoginButton';
import { FacebookLoginButton } from '../../../../../../components/FacebookLoginButton';
import { GoogleLoginButton } from '../../../../../../components/GoogleLoginButton/GoogleLoginButton';
import { LinkAccountsModal } from '../../../../../../components/LinkAccountsModal';
import {
  DATA_TEST_IDS,
  DataTestIds,
  APPLE_SOCIAL_SIGN_IN,
  FACEBOOK_SOCIAL_SIGN_IN,
  GOOGLE_SOCIAL_SIGN_IN,
  STATUS_CODE_CONFLICT,
  STATUS_CODE_PRECONDITION_FAILED,
  DeviceType,
  Auth,
  ErrorTypes,
} from '../../../../../../constants';
import { ROUTE_SIGNUP_WELCOME } from '../../../../../../constants/routes';
import {
  withHistory,
  WithHistoryInjectedProps,
} from '../../../../../../hocs';
import { useAppleLoginWithAuthentication } from '../../../../../../hooks/api/useAppleLoginWithAuthentication';
import { useFacebookLoginWithAuthentication } from '../../../../../../hooks/api/useFacebookLoginWithAuthentication';
import { useGoogleLogin } from '../../../../../../hooks/api/useGoogleLogin';
import { UseStepperReturnType } from '../../../../../../hooks/useStepper';
import { authSlice } from '../../../../../../slices/auth';
import { usePostAuthenticationUpgradeMutation } from '../../../../../../slices/auth/v6/authApi';
import { wagApi } from '../../../../../../types/wagapi';
import { ApiErrorV6 } from '../../../../../../utils/buildApiErrorPipeline';
import { tryCatch } from '../../../../../../utils/tryCatch';

export type LoginByEmailFormWithSocialLoginsStepProps =
  UseStepperReturnType & ButtonProps & WithHistoryInjectedProps;
const LoginByEmailFormWithSocialLoginsStep = ({
  handleNext,
  forceCompleteSteps,
  setMeta,
  meta,
  history,
  ...rest
}: LoginByEmailFormWithSocialLoginsStepProps) => {
  const handleSignInWithEmail = () => {
    handleNext();
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [
    executePostAuthenticationUpgrade,
    {
      // TODO display error message for upgrade error
      error: postAuthenticationUpgradeErrorMessage,
      isLoading: isPostAuthenticationUpgradeLoading,
      isSuccess: isPostAuthenticationUpgradeSuccess,
    },
  ] = usePostAuthenticationUpgradeMutation();

  // Facebook login authentication logic
  const {
    execute: executeFacebookLoginWithAuthentication,
    accessToken: facebookLoginAccessToken,
    facebookLoginSubmitting,
    facebookAuthenticationSubmitting,
    facebookAuthenticationIsError,
  } = useFacebookLoginWithAuthentication();

  const dispatch = useDispatch();

  const handleSocialProviderResponse = (response: wagApi.PostSocialAuthenticationResponse) => {
    const {
      newAccount,
    } = response;

    if (newAccount) {
      history.push({
        path: ROUTE_SIGNUP_WELCOME,
      });
      return;
    }

    // Else if newAccount: false
    executePostAuthenticationUpgrade({
      deviceType: DeviceType.Web,
      id: response.id,
      token: response.token,
      userRoleType: Auth.RoleType.Owner,
    });
  };

  const handleSocialSignInEmailNotProvidedError = (
    error: ApiErrorV6,
    socialProvider: string,
  ) => {
    /* In the case of a 412 status code error with
        type _auth.social_provider_missing_scope
        the error message needs to be dynamically updated
        to contain the social provider in question
      */
    const updatedErrorMessage = error.message.replace('the social provider', socialProvider);
    return { ...error, message: updatedErrorMessage };
  };

  const [facebookAuthenticationError,
    setFacebookAuthenticationError] = useState(null as ApiErrorV6);

  const handleFacebookLoginButtonClicked = async () => {
    let [response, error] = await tryCatch(() => executeFacebookLoginWithAuthentication());
    if (error) {
      const {
        status,
        type,
      } = error;
      if (status === STATUS_CODE_CONFLICT) {
        dispatch(authSlice.actions.setSocialAuthProviderToLink(FACEBOOK_SOCIAL_SIGN_IN));
        setIsModalOpen(true);
        return;
      }

      if (status === STATUS_CODE_PRECONDITION_FAILED
        && type === ErrorTypes.SOCIAL_PROVIDER_MISSING_SCOPE) {
        error = handleSocialSignInEmailNotProvidedError(error, FACEBOOK_SOCIAL_SIGN_IN);
      }
      setFacebookAuthenticationError(error);
      return;
    }

    if (response) {
      handleSocialProviderResponse(response);
    }
  };

  // Google login authentication logic
  const {
    error: googleLoginError,
    errorMessage: googleLoginErrorMessage,
    googleIdToken,
    execute: executeGoogleLogin,
    googleAuthenticationResponse,
  } = useGoogleLogin();

  useEffect(() => {
    // TODO remove fb and apple
    if (googleLoginError) {
      return;
    }
    /*
      TODO Move this logic and other google redirect logic on signin
      handleSocialProviderResponse once tests are written for Google sign in
    */
    if (googleAuthenticationResponse && googleAuthenticationResponse.newAccount) {
      history.push({
        path: ROUTE_SIGNUP_WELCOME,
      });
    }
  }, [googleLoginError, googleAuthenticationResponse, history]);

  useEffect(() => {
    if (!googleLoginError || !googleIdToken) {
      return;
    }

    if ((googleLoginErrorMessage as ApiErrorV6).status === STATUS_CODE_CONFLICT) {
      dispatch(authSlice.actions.setSocialAuthProviderToLink(GOOGLE_SOCIAL_SIGN_IN));
      setIsModalOpen(true);
    }
  }, [
    googleLoginError,
    googleIdToken,
    googleLoginErrorMessage,
    dispatch,
  ]);

  const handleOnGoogleLoginSuccess = async (googleLoginResponse: GoogleLoginResponse) => {
    const [, error] = await tryCatch(() => executeGoogleLogin(googleLoginResponse));

    if (error) {
      return;
    }

    /*
      Last remaining scenario is if the response
      is a 200 newAccount: false  and we log into the app
    */
    forceCompleteSteps();
  };

  // Apple login authentication logic

  const {
    executeAppleLoginWithAuthentication,
  } = useAppleLoginWithAuthentication();

  const [appleLoginWithAuthenticationError, setAppleLoginWithAuthenticationError] = useState(null);

  const handleAppleLoginButtonClick = async () => {
    /*
      Since the isError or error state are not returned from the socialAuthApi hook
      for authentication with Apple, a different approach of awaiting the response from
      the execute function is required here.
    */

    /*
      possible reassignment of error means a let is
      necessary for the error variable returned from the try catch
      and by extension the response variable also.
    */
    // eslint-disable-next-line prefer-const
    let [response, error] = await tryCatch(() => executeAppleLoginWithAuthentication());

    if (error) {
      if (error.status === STATUS_CODE_CONFLICT) {
        dispatch(authSlice.actions.setSocialAuthProviderToLink(APPLE_SOCIAL_SIGN_IN));
        setIsModalOpen(true);
        return;
      }

      if (error.status === STATUS_CODE_PRECONDITION_FAILED
        && error.type === ErrorTypes.SOCIAL_PROVIDER_MISSING_SCOPE) {
        error = handleSocialSignInEmailNotProvidedError(error, APPLE_SOCIAL_SIGN_IN);
      }
      setAppleLoginWithAuthenticationError(error);
    }

    if (response) {
      handleSocialProviderResponse(response);
    }
  };

  useEffect(() => {
    if (!isPostAuthenticationUpgradeSuccess) {
      return;
    }
    forceCompleteSteps();
  }, [isPostAuthenticationUpgradeSuccess, forceCompleteSteps]);

  return (
    <Grid
      justifyItems="center"
      gridRowGap="10px"
      justifyContent="center"
    >

      <GoogleLoginButton
        onSuccess={handleOnGoogleLoginSuccess}
      />

      <AppleLoginButton
        handleAppleLoginButtonClick={handleAppleLoginButtonClick}
      />

      <LinkAccountsModal
        handleNext={handleNext}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <Button
        variant="contained"
        gridColumn="1/span 1"
        type="button"
        width="280px"
        mb="24px"
        fontSize="18px"
        data-testId={DATA_TEST_IDS.LOGIN.loginForm.SignInWithEmailBtn}
        {...rest}
        onClick={handleSignInWithEmail}
      >
        Sign in with email
      </Button>

      {/* // TODO  Dry up these error messages */}
      {googleLoginError
      && (googleLoginErrorMessage as ApiErrorV6).status !== STATUS_CODE_CONFLICT && (
        <Typography
          data-testid={DataTestIds.Login.loginForm.GoogleSocialLoginErrorMessage}
          my="16px"
          color="red"
        >
          {(googleLoginErrorMessage as ApiErrorV6).message}
        </Typography>
      )}

      {facebookAuthenticationIsError
      && facebookAuthenticationError
      && facebookAuthenticationError.status !== STATUS_CODE_CONFLICT && (
        <Typography
          data-testid={DataTestIds.Login.loginForm.FacebookSocialLoginErrorMessage}
          mx="16px"
          color="red"
        >
          {facebookAuthenticationError.message}
        </Typography>
      )}

      {appleLoginWithAuthenticationError && (
        <Typography
          data-testid={DataTestIds.Login.loginForm.AppleSocialLoginErrorMessage}
          mx="16px"
          pt="16px"
          px="16px"
          color="red"
        >
          {(appleLoginWithAuthenticationError as ApiErrorV6).message}
        </Typography>
      )}

      {/* TODO add postAuthenticationUpgrade error field */}

    </Grid>
  );
};

const EnhancedLoginByEmailFormWithSocialLoginsStep = withHistory(
  LoginByEmailFormWithSocialLoginsStep,
);
export { EnhancedLoginByEmailFormWithSocialLoginsStep as LoginByEmailFormWithSocialLoginsStep };
